import { useState, useCallback } from 'react'

import { useGetAccountantRefereeQuery } from '@/lib/apollo'
import type { AccountantReferee } from '@/lib/apollo/query/get-accountant-referee'
import QUERIES from '@/lib/graphql'
import type { Application } from '@/types/Activity'

import { graphQLQuery } from './hooks'

export type FetchSingleApplication = (
  customerId: string,
  subjectId: string,
) => Promise<void>

const useAccountantReferee = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [accountantReferee, setAccountantReferee] = useState<
    AccountantReferee | undefined
  >(undefined)
  const [application, setApplication] = useState<Application | undefined>(undefined)

  const [fetchAccountantReferee] = useGetAccountantRefereeQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  })

  const fetchSingleApplication = useCallback<FetchSingleApplication>(
    async (customerId: string, subjectId: string) => {
      setLoading(true)
      setApplication(undefined)
      setAccountantReferee(undefined)
      setError(undefined)

      try {
        const { data: employerRefereeData } = await fetchAccountantReferee({
          variables: {
            customerId,
            subjectId,
          },
        })

        if (!employerRefereeData) {
          throw Error('No subject was retrieved')
        }

        setAccountantReferee(employerRefereeData?.getAccountantReferee)

        const getSingleApplicationResponse = await graphQLQuery({
          query: QUERIES.getSingleApplication,
          variables: {
            customerId,
            applicationId: employerRefereeData?.getAccountantReferee.applicationId,
            subjectId,
          },
          endpoint: `${import.meta.env.VITE_REPORTS_GRAPHQL}`,
        })

        const data = await getSingleApplicationResponse.json()

        if (!data || !data.data) {
          throw Error('Get single application returned undefined')
        }

        setApplication(data.data.getDashboardApplication)
      } catch (e) {
        console.error(e)
        setError('Failed to retrieve applicant')
      } finally {
        setLoading(false)
      }
    },
    [fetchAccountantReferee, setLoading, setError, setApplication],
  )

  return {
    loading,
    error,
    accountantReferee,
    application,
    fetchSingleApplication,
  }
}

export default useAccountantReferee
